import { useState } from 'react'
import { Check, Pencil, Plus, Trash2, X } from 'lucide-react'
import FolderIcon from '@/features/lists/FolderIcon'
import { FOLDER_COLORS } from '@/features/lists/folderColors'
import ConfirmButton from '@/components/ui/ConfirmButton'
import { useCreateFolder, useDeleteFolder, useListFolders, useUpdateFolder } from '@/features/lists/useLists'
import { apiErrorMessage } from '@/features/settings/apiErrorMessage'
import { cn } from '@/lib/cn'
import type { ListFolder } from '@/api/lists'

const inputCls =
  'w-full px-2.5 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/40'

function FolderEditor({
  initial,
  pending,
  error,
  onSave,
  onCancel,
}: {
  initial?: ListFolder
  pending: boolean
  error: unknown
  onSave: (input: { name: string; color: string | null }) => void
  onCancel: () => void
}) {
  const [name, setName] = useState(initial?.name ?? '')
  const [color, setColor] = useState<string | null>(initial?.color ?? FOLDER_COLORS[0])
  return (
    <form
      className="rounded-lg border border-gray-200 bg-gray-50 p-2.5 space-y-2"
      onSubmit={(e) => {
        e.preventDefault()
        if (name.trim()) onSave({ name: name.trim(), color })
      }}
    >
      <input autoFocus className={inputCls} value={name} onChange={(e) => setName(e.target.value)} placeholder="Folder name" />
      <div className="flex flex-wrap gap-1.5" role="radiogroup" aria-label="Folder colour">
        {FOLDER_COLORS.map((c) => (
          <button
            key={c}
            type="button"
            role="radio"
            aria-checked={color === c}
            aria-label={c}
            onClick={() => setColor(c)}
            className={cn('w-5 h-5 rounded-full ring-offset-1', color === c && 'ring-2 ring-brand-darkest')}
            style={{ backgroundColor: c }}
          />
        ))}
      </div>
      {error != null && <p className="text-danger text-xs">{apiErrorMessage(error)}</p>}
      <div className="flex gap-2">
        <button
          type="submit"
          disabled={!name.trim() || pending}
          className="px-3 py-1.5 bg-brand-primary text-white rounded-lg text-xs font-medium hover:bg-brand-dark disabled:opacity-60"
        >
          {pending ? 'Saving…' : initial ? 'Save' : 'Create folder'}
        </button>
        <button type="button" onClick={onCancel} className="px-2 py-1.5 text-xs text-gray-500 hover:text-brand-dark">
          Cancel
        </button>
      </div>
    </form>
  )
}

/**
 * The folders beside the lists table: pick one to see its lists, create, rename/recolour and delete
 * (deleting keeps the lists, just un-filed).
 */
export default function FoldersPanel({
  selected,
  onSelect,
  onClose,
}: {
  selected: string
  onSelect: (folderId: string) => void
  onClose: () => void
}) {
  const { data: folders } = useListFolders()
  const create = useCreateFolder()
  const update = useUpdateFolder()
  const del = useDeleteFolder()
  const [adding, setAdding] = useState(false)
  const [editing, setEditing] = useState<string | null>(null)
  const [query, setQuery] = useState('')
  const shown = (folders ?? []).filter((f) => f.name.toLowerCase().includes(query.trim().toLowerCase()))

  return (
    <aside className="bg-white border border-gray-200 rounded-xl p-4 space-y-3 self-start" aria-label="Folders">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-brand-darkest">Folders</h2>
        <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600" aria-label="Close folders">
          <X size={16} />
        </button>
      </div>
      {adding ? (
        <FolderEditor
          pending={create.isPending}
          error={create.error}
          onCancel={() => { setAdding(false); create.reset() }}
          onSave={(input) => create.mutate(input, { onSuccess: () => setAdding(false) })}
        />
      ) : (
        <button
          type="button"
          onClick={() => setAdding(true)}
          className="w-full flex items-center justify-center gap-1.5 px-3 py-2 bg-brand-primary text-white rounded-lg text-sm font-medium hover:bg-brand-dark"
        >
          <Plus size={15} /> New folder
        </button>
      )}
      {(folders ?? []).length > 6 && (
        <input className={inputCls} value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search folders…" />
      )}
      <ul className="space-y-0.5">
        <li>
          <button
            type="button"
            onClick={() => onSelect('')}
            className={cn(
              'w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-sm',
              selected === '' ? 'bg-brand-primary/10 text-brand-primary font-medium' : 'text-gray-700 hover:bg-gray-50',
            )}
          >
            <FolderIcon color={null} open={selected === ''} />
            All lists
          </button>
        </li>
        {shown.map((f) =>
          editing === f.id ? (
            <li key={f.id}>
              <FolderEditor
                initial={f}
                pending={update.isPending}
                error={update.error}
                onCancel={() => { setEditing(null); update.reset() }}
                onSave={(input) => update.mutate({ id: f.id, input }, { onSuccess: () => setEditing(null) })}
              />
            </li>
          ) : (
            <li key={f.id} className="group flex items-center gap-1">
              <button
                type="button"
                onClick={() => onSelect(f.id)}
                className={cn(
                  'flex-1 min-w-0 flex items-center gap-2 px-2 py-1.5 rounded-lg text-sm text-left',
                  selected === f.id ? 'bg-brand-primary/10 text-brand-primary font-medium' : 'text-gray-700 hover:bg-gray-50',
                )}
              >
                {selected === f.id ? <Check size={15} className="shrink-0" /> : <FolderIcon color={f.color} />}
                <span className="truncate flex-1">{f.name}</span>
                <span className="text-xs text-gray-400 tabular-nums">{f.listCount}</span>
              </button>
              <button
                type="button"
                onClick={() => setEditing(f.id)}
                className="p-1 text-gray-300 hover:text-brand-primary opacity-0 group-hover:opacity-100 focus:opacity-100"
                title={`Rename ${f.name}`}
              >
                <Pencil size={13} />
              </button>
              <span className="opacity-0 group-hover:opacity-100 focus-within:opacity-100">
                <ConfirmButton
                  onConfirm={() => {
                    if (selected === f.id) onSelect('')
                    del.mutate(f.id)
                  }}
                  title={`Delete ${f.name} — its lists stay, un-filed`}
                >
                  <Trash2 size={13} />
                </ConfirmButton>
              </span>
            </li>
          ),
        )}
      </ul>
      {(folders ?? []).length === 0 && !adding && (
        <p className="text-xs text-gray-400">Group lists into folders — e.g. Buyers, Sellers, Past clients.</p>
      )}
    </aside>
  )
}
